import React from 'react';
import {useMatch, useNavigate} from 'react-router-dom';
import {HomeIcon} from 'lucide-react';
import {GUESTAPP_NAVIGATION_MODE} from '@chekin/core';
import Menu from '../Menu';
import {PATHS} from 'routes/constants';
import {useSummary} from 'context/summary';
import {useReservation} from 'context/reservation';
import {useCustomTheme} from 'context/customTheme';
import {usePath} from 'hooks';
import {DEVICE, useScreenResize} from '@chekin/guestapp-ui-kit';
import {ShoppingCartButton} from '../ShoppingCartButton';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {
  DiscardChangesModal,
  DiscardChangesModalRef,
} from '../DiscardChangesModal/DiscardChangesModal';
import {
  HousingLongNameModal,
  HousingLongNameModalPropsRef,
} from '../HousingLongNameModal/HousingLongNameModal';
import {LanguageButton} from 'components/LanguageButton';
import {
  Container,
  HeaderWrapper,
  HeaderTitle,
  LogoContainer,
  Title,
  ContentIcons,
} from './styled';

type NavbarProps = {
  visible?: boolean;
  title?: string;
};

type AppHeaderSettings = {
  navigationTitleConfig: GUESTAPP_NAVIGATION_MODE;
  logo?: string | null;
};

function getHousingImgSrc(appSettingTemplate: AppHeaderSettings, housingLogo?: string) {
  const showLogo = [
    GUESTAPP_NAVIGATION_MODE.logo,
    GUESTAPP_NAVIGATION_MODE.property_name_and_logo,
  ].includes(appSettingTemplate.navigationTitleConfig);

  if (!showLogo && !housingLogo) {
    return;
  }

  return appSettingTemplate.logo || housingLogo;
}

const Navbar = ({visible, title}: NavbarProps) => {
  const {data} = useSummary();
  const {housing} = useReservation();
  const {data: templateData} = useCustomTheme();
  const discardChangesModalRef = React.useRef<DiscardChangesModalRef>(null);
  const housingLongNameModalRef = React.useRef<HousingLongNameModalPropsRef>(null);
  const isHomeView = useMatch('/:token');
  const name = title || data?.display_name || data?.property_name || '';
  const housingNameLength = name?.length || 0;
  const {isMatch: isMobileMode} = useScreenResize(DEVICE.laptop);
  const layoutMode = useLayoutMode();
  const handleClickOnTitle = React.useCallback(() => {
    if (!isMobileMode) return;
    if (isHomeView && housingNameLength > 20) {
      housingLongNameModalRef.current?.open();
      return;
    }
    discardChangesModalRef.current?.open(PATHS.home);
  }, [discardChangesModalRef, isHomeView, housingNameLength, isMobileMode]);

  const titleLogo = templateData?.navigation_title_logo;
  const titleLogoType =
    templateData?.navigation_title_type || GUESTAPP_NAVIGATION_MODE.property_name;

  const appSettingTemplate: AppHeaderSettings = {
    navigationTitleConfig: titleLogoType,
    logo: titleLogo,
  };
  const housingImg = getHousingImgSrc(appSettingTemplate, housing?.picture?.src);
  const showLogo =
    appSettingTemplate.navigationTitleConfig !== GUESTAPP_NAVIGATION_MODE.property_name &&
    titleLogoType;

  return (
    <>
      <HeaderWrapper $visible={visible || layoutMode === 'kiosko'} className="navbar">
        <Container className="navbar__container kiosko:h-[150px] kiosko:bg-white kiosko:px-14">
          {layoutMode === 'kiosko' && <HomeKioskoIcon />}
          {showLogo && (
            <LogoContainer className="navbar__logo">
              <img
                src={housingImg}
                width={35}
                height={35}
                alt="housing Logo"
                className="navbar__logo-img"
              />
            </LogoContainer>
          )}

          {titleLogoType !== GUESTAPP_NAVIGATION_MODE.logo ||
          (titleLogoType === GUESTAPP_NAVIGATION_MODE.logo && !isMobileMode) ? (
            <HeaderTitle onClick={handleClickOnTitle} className="navbar__title">
              <Title className="navbar__title-text kiosko:text-kiosko-s">{name}</Title>
            </HeaderTitle>
          ) : null}
          {layoutMode === 'default' && (
            <ContentIcons>
              <ShoppingCartButton />
              <Menu showLogo={Boolean(showLogo)} />
            </ContentIcons>
          )}
          {layoutMode === 'kiosko' && <LanguageButton />}
        </Container>
      </HeaderWrapper>
      <DiscardChangesModal ref={discardChangesModalRef} />
      <HousingLongNameModal ref={housingLongNameModalRef} name={name} />
    </>
  );
};

const HomeKioskoIcon = () => {
  const {getFullPath} = usePath();
  const navigate = useNavigate();

  const goToHome = () => {
    navigate(getFullPath(PATHS.home));
  };
  return <HomeIcon size={48} onClick={goToHome} />;
};

export {Navbar};
export type {NavbarProps};
